import { useState } from 'react';
import { connect } from 'react-redux';

import { exists } from 'lib/types';

import { useAppSelector } from 'redux/hooks';
import { AuthState, selectUser } from 'redux/auth';
import LoadingState from 'components/LoadingState';
import { InvoiceType } from 'lib/types/invoices';
import { getUserStripeId } from 'lib/utils/users';
import { logInfo } from 'utils/logger';
import { getLocationParams } from 'lib/frontend/utils/browser';
import { Product } from 'lib/enums';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { ColumnService } from 'lib/services/directory';
import { Alert } from 'lib/components/Alert';
import PayInvoicePage from './PayInvoicePage';
import { getSavedPaymentsAccess } from './helpers/getSavedPaymentsAccess';
import { getPayInvoicePaymentData } from './helpers/loadPayInvoiceData';

const mapStateToProps = (state: { auth: AuthState }) => ({
  authLoading: state.auth.loading
});

export type PayInvoicePaymentMethodsType =
  | 'card'
  | 'saved-card'
  | 'saved-bank'
  | 'saved-ach';

type PayInvoiceProps = {
  authLoading: boolean;
};

function PayInvoice({ authLoading }: PayInvoiceProps) {
  const user = useAppSelector(selectUser);
  const enableAuthCapture = !!getLocationParams().get('enableAuthCapture');
  const [product, setProduct] = useState<Product>(Product.Notice);

  const url = window.location.href;
  const invoiceId = window.location.href.split('/')[4];
  const onLoadingTimeout = () => {
    logInfo('PayInvoice timed out', {
      payInvoiceData: !!payInvoiceData,
      url
    });
  };

  // Fetch the invoice data. This does not depend on auth state, so it runs
  // exactly once per invoice rather than re-fetching when auth resolves.
  const { value: invoiceDataResult, error: payInvoiceError } = useAsyncEffect({
    fetchData: async () => {
      const { error: getDataError, response: payInvoiceData } =
        await getPayInvoicePaymentData(invoiceId);
      if (getDataError || !payInvoiceData) {
        if (getDataError?.message.includes('not found')) {
          return { invoiceNotFound: true };
        }
        throw getDataError ?? new Error('Failed to load PayInvoice data');
      }
      if (payInvoiceData.type === InvoiceType.ORDER) {
        setProduct(payInvoiceData.product);
      }
      return { payInvoiceData };
    },
    dependencies: [invoiceId],
    errorConfig: {
      service: ColumnService.PAYMENTS,
      message: 'Error fetching payInvoiceData',
      tags: {
        invoiceId
      }
    }
  });
  const { payInvoiceData, invoiceNotFound } = invoiceDataResult ?? {};

  // Compute saved payments access once both the invoice data and auth have
  // resolved. Kept separate from the invoice fetch so that waiting on auth
  // does not trigger a redundant re-fetch of the invoice data.
  const { value: savedPaymentsAccess } = useAsyncEffect({
    fetchData: async () => {
      // Wait for auth to resolve before computing saved payments access,
      // otherwise we momentarily treat the user as logged out.
      if (!payInvoiceData || authLoading) {
        return null;
      }
      let userStripeId: string | undefined;
      if (exists(user)) {
        const userActiveOrganization = await user
          .data()
          .activeOrganization?.get();
        userStripeId = getUserStripeId(user, userActiveOrganization);
      }
      return getSavedPaymentsAccess(payInvoiceData, user, userStripeId);
    },
    dependencies: [user?.id, authLoading, !!payInvoiceData],
    errorConfig: {
      service: ColumnService.PAYMENTS,
      message: 'Error computing savedPaymentsAccess',
      tags: {
        invoiceId
      }
    }
  });
  const invoicePricingData = payInvoiceData?.invoicePricingData;

  const isMissingData =
    !invoicePricingData || !payInvoiceData || !savedPaymentsAccess;

  if (isMissingData && !invoiceNotFound && !payInvoiceError) {
    return (
      <LoadingState
        context={{ location: 'PayInvoice' }}
        onTimeout={onLoadingTimeout}
      />
    );
  }
  if (payInvoiceError || invoiceNotFound || isMissingData) {
    const errorMessage = invoiceNotFound
      ? 'Invoice could not be found. Please check the link you used and try again.'
      : 'Failed to load data to pay invoice. Please try again.';
    return <Alert id="pay-invoice-error" status="error" title={errorMessage} />;
  }

  return (
    <PayInvoicePage
      payInvoiceData={payInvoiceData}
      invoicePricingData={invoicePricingData}
      savedPaymentsAccess={savedPaymentsAccess}
      user={user}
      enableAuthCapture={enableAuthCapture}
      product={product}
    />
  );
}

export default connect(mapStateToProps)(PayInvoice);
